/**
 * Cliente HTTP — porta única de saída para o backend.
 *
 * Não existe `fetch` solto em nenhum outro lugar do app. Isso substitui:
 *  - os 4 wrappers de HTTP diferentes do sistema antigo;
 *  - o monkey-patch global de `window.fetch` (index.html:4442);
 *  - os 6 `.catch(function(){})` vazios que deixavam a tela presa em
 *    "Carregando…" para sempre (B-23).
 *
 * **Escopo de dados (decisão D23):** o sistema atende UMA empresa por instalação.
 * Este cliente **não envia identificador de empresa** — nem `cnpj_raiz` nem
 * equivalente. O backend resolve o escopo a partir do usuário autenticado.
 * Se algum dia houver multi-empresa, o filtro continua sendo responsabilidade do
 * servidor: parâmetro vindo do cliente não é controle de acesso.
 */

import {
  ApiError,
  ehBloqueioSiscomex,
  ehHtml,
  ehSessaoDoApp,
  extrairCodigoSiscomex,
  extrairDetalhe,
  mensagemPadrao,
} from './errors'

const TOKEN_KEY = 'autocomex_token'

/* --------------------------------------------------------------- sessão */

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY)
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t)

/** Limpa a sessão. Inclui o cache do service worker (corrige S-07). */
export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  navigator.serviceWorker?.controller?.postMessage({ type: 'LOGOUT' })
}

/** Emitido quando a sessão do app cai — o AppShell escuta e redireciona. */
export const SESSAO_EXPIRADA = 'autocomex:sessao-expirada'
/** Emitido quando o SISCOMEX está desconectado — mostra o banner de reconexão. */
export const SISCOMEX_DESCONECTADO = 'autocomex:siscomex-desconectado'

/* ---------------------------------------------------------------- request */

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Corpo JSON. Não usar junto de `form`. */
  body?: unknown
  /** Corpo multipart (upload). Não usar junto de `body`. */
  form?: FormData
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
  /** Milissegundos. O padrão é 30 s — o Cloudflare corta em 100 s, então
   *  operação longa deve ser job assíncrono, não timeout maior. */
  timeout?: number
  /** Chave de idempotência para POST que inicia job (ver REDESIGN_JOBS_SPEC.md). */
  idempotencyKey?: string
}

function montarUrl(path: string, query?: RequestOpts['query']): string {
  const url = path.startsWith('/api') ? path : `/api${path}`
  if (!query) return url
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== null && v !== undefined && v !== '') qs.append(k, String(v))
  }
  const s = qs.toString()
  return s ? `${url}?${s}` : url
}

/**
 * GET é idempotente: timeout ou queda de rede ganham uma segunda tentativa
 * antes de a tela desistir, porque lista presa em "Carregando..." custa mais
 * que meio segundo de espera. Mutação nunca repete — pode ter sido aplicada.
 */
export async function request<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  try {
    return await executar<T>(path, opts)
  } catch (e) {
    const metodo = opts.method ?? 'GET'
    const repetivel = e instanceof ApiError && (e.kind === 'timeout' || e.kind === 'rede')
    if (metodo !== 'GET' || !repetivel || opts.signal?.aborted) throw e
    return executar<T>(path, opts)
  }
}

async function executar<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, form, query, signal, timeout = 30_000, idempotencyKey } = opts

  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  let payload: BodyInit | undefined
  if (form) {
    payload = form // o browser define o boundary do multipart
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  // Timeout próprio + possibilidade de cancelamento externo.
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(new DOMException('timeout', 'TimeoutError')), timeout)
  signal?.addEventListener('abort', () => ctrl.abort(signal.reason), { once: true })

  let resp: Response
  try {
    resp = await fetch(montarUrl(path, query), {
      method,
      headers,
      body: payload,
      signal: ctrl.signal,
    })
  } catch (e) {
    clearTimeout(timer)
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ApiError(
        'timeout',
        method === 'GET'
          ? mensagemPadrao('timeout')
          : 'A operação demorou mais do que o permitido e foi interrompida. Ela pode ter' +
            ' sido concluída no servidor: recarregue a tela e confira antes de repetir.',
      )
    }
    if (e instanceof DOMException && e.name === 'AbortError') throw e // cancelamento intencional
    throw new ApiError('rede', mensagemPadrao('rede'), { detail: e })
  } finally {
    clearTimeout(timer)
  }

  const texto = await resp.text()

  // Resposta HTML: página de erro do nginx/Cloudflare. É a origem clássica do
  // "Unexpected token '<'". Traduzimos em vez de deixar o JSON.parse explodir.
  if (ehHtml(texto)) {
    throw new ApiError('resposta_invalida', mensagemPadrao('resposta_invalida'), {
      status: resp.status,
      detail: texto.slice(0, 500),
    })
  }

  let corpo: unknown = null
  if (texto) {
    try {
      corpo = JSON.parse(texto)
    } catch {
      if (resp.ok) {
        throw new ApiError('resposta_invalida', mensagemPadrao('resposta_invalida'), {
          status: resp.status,
          detail: texto.slice(0, 500),
        })
      }
    }
  }

  const detalhe = extrairDetalhe(corpo) ?? (typeof corpo === 'string' ? corpo : '') ?? ''
  const textoBusca = `${detalhe} ${texto.slice(0, 800)}`

  if (!resp.ok) throw erroDeResposta(resp.status, detalhe, textoBusca, corpo)

  /**
   * Contrato peculiar e proposital: a retificação (e outras rotas) devolve
   * 200 com `success: false` para o Cloudflare não sequestrar a resposta de erro
   * — ver 09_RETIFICACAO_DUIMP.md linha 25. Então status 200 não basta.
   */
  if (corpo && typeof corpo === 'object' && (corpo as Record<string, unknown>).success === false) {
    const codigo = extrairCodigoSiscomex(textoBusca)
    // Rotas locais (extração por IA, leitura de planilha) também devolvem
    // success:false; sem código nem menção ao Portal Único o erro não é dele.
    if (!codigo && !/siscomex|portal\s*único|portal\s*unico/i.test(textoBusca)) {
      throw new ApiError('operacao_falhou', detalhe || mensagemPadrao('operacao_falhou'), {
        detail: corpo,
      })
    }
    const msg = detalhe || 'O SISCOMEX recusou a operação.'
    if (ehBloqueioSiscomex(textoBusca)) {
      throw new ApiError('siscomex_bloqueado', mensagemPadrao('siscomex_bloqueado'), {
        retryAfter: 60,
        siscomexCode: codigo,
        detail: corpo,
      })
    }
    throw new ApiError('siscomex_erro', msg, { siscomexCode: codigo, detail: corpo })
  }

  return corpo as T
}

/* ------------------------------------------------------- classificação de erro */

function erroDeResposta(
  status: number,
  detalhe: string,
  textoBusca: string,
  corpo: unknown,
): ApiError {
  const codigo = extrairCodigoSiscomex(textoBusca)

  if (status === 401) {
    // Distinção crítica, preservada do sistema atual: só o 401 do APP desloga.
    // O 401 do SISCOMEX apenas pede reconexão — deslogar aqui era um bug antigo.
    if (ehSessaoDoApp(textoBusca)) {
      window.dispatchEvent(new CustomEvent(SESSAO_EXPIRADA))
      return new ApiError('sessao_expirada', mensagemPadrao('sessao_expirada'), { status })
    }
    window.dispatchEvent(new CustomEvent(SISCOMEX_DESCONECTADO))
    return new ApiError('siscomex_desconectado', mensagemPadrao('siscomex_desconectado'), {
      status,
      siscomexCode: codigo,
      detail: corpo,
    })
  }

  if (ehBloqueioSiscomex(textoBusca)) {
    return new ApiError('siscomex_bloqueado', mensagemPadrao('siscomex_bloqueado'), {
      status,
      retryAfter: 60,
      siscomexCode: codigo,
      detail: corpo,
    })
  }

  // 503 com {offline:true} vem do nosso service worker.
  if (
    status === 503 &&
    corpo &&
    typeof corpo === 'object' &&
    (corpo as Record<string, unknown>).offline
  ) {
    return new ApiError('offline', mensagemPadrao('offline'), { status })
  }

  const mapa: Record<number, ApiError> = {
    403: new ApiError('sem_permissao', detalhe || mensagemPadrao('sem_permissao'), { status }),
    404: new ApiError('nao_encontrado', detalhe || mensagemPadrao('nao_encontrado'), { status }),
    408: new ApiError('timeout', mensagemPadrao('timeout'), { status }),
    504: new ApiError('timeout', mensagemPadrao('timeout'), { status }),
  }
  if (mapa[status]) return mapa[status]

  if (status === 400 || status === 422) {
    return new ApiError('validacao', detalhe || mensagemPadrao('validacao'), {
      status,
      siscomexCode: codigo,
      detail: corpo,
    })
  }

  if (status >= 500) {
    return new ApiError('servidor', detalhe || mensagemPadrao('servidor'), {
      status,
      siscomexCode: codigo,
      detail: corpo,
    })
  }

  return new ApiError('desconhecido', detalhe || mensagemPadrao('desconhecido'), {
    status,
    detail: corpo,
  })
}

/* ------------------------------------------------------------- download */

/** Baixa um arquivo autenticado (PDF etc.) e dispara o download no browser. */
export async function baixarArquivo(
  path: string,
  nomeArquivo: string,
  opts: { method?: 'GET' | 'POST'; body?: unknown } = {},
): Promise<void> {
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  let payload: BodyInit | undefined
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(opts.body)
  }
  const resp = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: payload,
  })
  if (!resp.ok) {
    const texto = await resp.text()
    throw new ApiError('servidor', extrairDetalhe(safeJson(texto)) || `Falha ao gerar o arquivo (HTTP ${resp.status}).`, {
      status: resp.status,
    })
  }
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nomeArquivo
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t)
  } catch {
    return null
  }
}

/* ------------------------------------------------------------------- SSE */

/**
 * POST com resposta em Server-Sent Events. Cada `data: {...}` vira uma chamada
 * a `onEvent`; `data: [DONE]` encerra. Usado pelo chat da BHLogueirinha.
 */
export async function streamSse(
  path: string,
  body: unknown,
  onEvent: (ev: Record<string, unknown>) => void,
  timeout = 300_000,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(new DOMException('timeout', 'TimeoutError')), timeout)

  let resp: Response
  try {
    resp = await fetch(montarUrl(path), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: ctrl.signal,
    })
  } catch (e) {
    clearTimeout(timer)
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ApiError('timeout', mensagemPadrao('timeout'))
    }
    throw new ApiError('rede', mensagemPadrao('rede'), { detail: e })
  }

  if (!resp.ok || !resp.body) {
    clearTimeout(timer)
    const texto = await resp.text().catch(() => '')
    throw new ApiError('servidor', `Falha na IA (HTTP ${resp.status})`, {
      status: resp.status,
      detail: texto.slice(0, 300),
    })
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const partes = buffer.split('\n\n')
      buffer = partes.pop() ?? ''
      for (const bloco of partes) {
        const linha = bloco.split('\n').find((l) => l.startsWith('data: '))
        if (!linha) continue
        const dado = linha.slice(6)
        if (dado === '[DONE]') return
        try {
          onEvent(JSON.parse(dado) as Record<string, unknown>)
        } catch {
          /* linha malformada: ignora */
        }
      }
    }
  } finally {
    clearTimeout(timer)
    reader.releaseLock()
  }
}

/* ------------------------------------------------------------------ atalhos */

export const api = {
  get: <T>(path: string, query?: RequestOpts['query'], opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: RequestOpts) => request<T>(path, { ...opts, method: 'DELETE' }),
  upload: <T>(path: string, form: FormData, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'POST', form, timeout: opts?.timeout ?? 120_000 }),
}
